'use client';

import { useIsBreakpoint } from '@bridebook/aura';
import {
  FullPageModal,
  FullPageModalContent,
  FullPageModalFooter,
  FullPageModalHeader,
} from '../full-page-modal/full-page-modal';
import { ResponsiveModalContext, useResponsiveModalContext } from './responsive-modal-context';
import { RoundedModal, type RoundedModalContainerHeight } from '../rounded-modal/rounded-modal';
import { RoundedModalContent } from '../rounded-modal/rounded-modal-content';
import { RoundedModalFooter } from '../rounded-modal/rounded-modal-footer';
import { RoundedModalHeader, RoundedModalTitle } from '../rounded-modal/rounded-modal-header';
import { ComponentPropsWithoutRef, useMemo } from 'react';

type ResponsiveModalProps = Omit<ComponentPropsWithoutRef<typeof RoundedModal>, 'height'> &
  Omit<ComponentPropsWithoutRef<typeof FullPageModal>, 'isOpen' | 'onOpenChange'> & {
    isDesktop?: boolean;
    height?: RoundedModalContainerHeight;
  };

export const ResponsiveModal = ({
  children,
  isOpen,
  onOpenChange,
  isDesktop: isDesktopProp,
  height = 'fixed-height',
  ...props
}: ResponsiveModalProps) => {
  const isDesktopLocal = useIsBreakpoint('--breakpoint-xl');
  const isDesktop = isDesktopProp ?? isDesktopLocal;
  const contextValue = useMemo(() => ({ isDesktop }), [isDesktop]);

  return (
    <ResponsiveModalContext.Provider value={contextValue}>
      {isDesktop ? (
        <RoundedModal
          isOpen={isOpen}
          onOpenChange={onOpenChange}
          width="lg"
          height={height}
          {...props}>
          {children}
        </RoundedModal>
      ) : (
        <FullPageModal isOpen={isOpen} onOpenChange={onOpenChange} {...props}>
          {children}
        </FullPageModal>
      )}
    </ResponsiveModalContext.Provider>
  );
};

/**
 *************************************************************
 */

type ResponsiveModalHeaderProps = ComponentPropsWithoutRef<typeof RoundedModalHeader> &
  ComponentPropsWithoutRef<typeof FullPageModalHeader>;

export const ResponsiveModalHeader = ({ children, ...props }: ResponsiveModalHeaderProps) => {
  const { isDesktop } = useResponsiveModalContext();

  if (isDesktop) {
    return (
      <RoundedModalHeader {...props}>
        {children && <RoundedModalTitle>{children}</RoundedModalTitle>}
      </RoundedModalHeader>
    );
  }

  return <FullPageModalHeader {...props}>{children}</FullPageModalHeader>;
};

/**
 *************************************************************
 */

type ResponsiveModalContentProps = ComponentPropsWithoutRef<typeof RoundedModalContent> &
  ComponentPropsWithoutRef<typeof FullPageModalContent>;

export const ResponsiveModalContent = ({ children, ...props }: ResponsiveModalContentProps) => {
  const { isDesktop } = useResponsiveModalContext();

  if (isDesktop) {
    return <RoundedModalContent {...props}>{children}</RoundedModalContent>;
  }

  return <FullPageModalContent {...props}>{children}</FullPageModalContent>;
};

/**
 *************************************************************
 */

type ResponsiveModalFooterProps = ComponentPropsWithoutRef<typeof RoundedModalFooter> &
  ComponentPropsWithoutRef<typeof FullPageModalFooter>;

export const ResponsiveModalFooter = ({ children, ...props }: ResponsiveModalFooterProps) => {
  const { isDesktop } = useResponsiveModalContext();

  if (isDesktop) {
    return <RoundedModalFooter {...props}>{children}</RoundedModalFooter>;
  }

  return <FullPageModalFooter {...props}>{children}</FullPageModalFooter>;
};
